import { useState, useEffect } from 'react'
import { X, Search, AlertTriangle } from 'lucide-react'
import api from '../../core/services/api'
import type { RisorsaCalendario, TipoEvento, CommessaAttiva, FaseCalendario } from '../../types/calendario'
import { TIPO_EVENTO_CONFIG } from '../../types/calendario'

interface ClienteOption {
  id: string
  ragione_sociale: string
}

interface Props {
  date: string
  preselectedResourceId: string | null
  risorse: RisorsaCalendario[]
  onClose: () => void
  onCreated: (data?: any) => void
}

export default function EventoModal({ date, preselectedResourceId, risorse, onClose, onCreated }: Props) {
  const [tipoEvento, setTipoEvento] = useState<TipoEvento>('lavoro')
  const [commessaId, setCommessaId] = useState<string>('')
  const [faseId, setFaseId] = useState<string>('')
  const [clienteId, setClienteId] = useState<string>('')
  const [selectedRisorse, setSelectedRisorse] = useState<Set<string>>(new Set())
  const [ore, setOre] = useState(8)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Data sources
  const [commesse, setCommesse] = useState<CommessaAttiva[]>([])
  const [fasi, setFasi] = useState<FaseCalendario[]>([])
  const [clienti, setClienti] = useState<ClienteOption[]>([])
  const [commessaSearch, setCommessaSearch] = useState('')
  const [clienteSearch, setClienteSearch] = useState('')
  const [risorseSearch, setRisorseSearch] = useState('')

  // Preselect resource
  useEffect(() => {
    if (preselectedResourceId) {
      setSelectedRisorse(new Set([preselectedResourceId]))
    }
  }, [preselectedResourceId])

  // Load commesse attive
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/v1/calendario/commesse-attive')
        setCommesse(res.data)
      } catch { /* ignore */ }
    }
    load()
  }, [])

  // Load clienti when tipo = visita_commerciale
  useEffect(() => {
    if (tipoEvento !== 'visita_commerciale') return
    const load = async () => {
      try {
        const res = await api.get('/v1/calendario/clienti')
        setClienti(res.data)
      } catch { /* ignore */ }
    }
    load()
  }, [tipoEvento])

  // Load fasi when commessa changes
  useEffect(() => {
    if (!commessaId) {
      setFasi([])
      setFaseId('')
      return
    }
    const load = async () => {
      try {
        const res = await api.get(`/v1/calendario/fasi-commessa/${commessaId}`)
        setFasi(res.data)
      } catch { /* ignore */ }
    }
    load()
  }, [commessaId])

  // Reset selections when tipo changes
  useEffect(() => {
    setCommessaId('')
    setFaseId('')
    setClienteId('')
    if (tipoEvento === 'visita_commerciale') {
      setOre(2)
    } else if (tipoEvento === 'lavoro') {
      setOre(8)
    }
  }, [tipoEvento])

  const toggleRisorsa = (id: string) => {
    setSelectedRisorse(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const filteredCommesse = commesse.filter(c =>
    !commessaSearch || `${c.numero} ${c.titolo} ${c.cliente}`.toLowerCase().includes(commessaSearch.toLowerCase())
  )

  const filteredClienti = clienti.filter(c =>
    !clienteSearch || c.ragione_sociale.toLowerCase().includes(clienteSearch.toLowerCase())
  )

  const filteredRisorse = risorse.filter(r =>
    !risorseSearch || r.label.toLowerCase().includes(risorseSearch.toLowerCase())
  )

  // Group risorse by gruppo
  const risorseByGruppo: Record<string, RisorsaCalendario[]> = {}
  for (const r of filteredRisorse) {
    if (!risorseByGruppo[r.gruppo]) risorseByGruppo[r.gruppo] = []
    risorseByGruppo[r.gruppo].push(r)
  }

  const selectedFase = fasi.find(f => f.id === faseId)
  const isLavoro = tipoEvento === 'lavoro'
  const isVisita = tipoEvento === 'visita_commerciale'

  const handleSubmit = async () => {
    if (selectedRisorse.size === 0) {
      setError('Seleziona almeno una risorsa')
      return
    }
    if (isLavoro && !commessaId) {
      setError('Seleziona una commessa per eventi di tipo lavoro')
      return
    }
    if (isVisita && !clienteId) {
      setError('Seleziona un cliente per la visita commerciale')
      return
    }

    setSaving(true)
    setError('')

    // Convert selected FC resource IDs back to tipo+id pairs
    const risorsePayload = Array.from(selectedRisorse).map(fcId => {
      const r = risorse.find(ri => ri.id === fcId)
      return { tipo: r?.tipo || 'personale', id: r?.resource_id || '' }
    }).filter(r => r.id)

    try {
      const resp = await api.post('/v1/calendario/eventi', {
        data: date,
        tipo_evento: tipoEvento,
        commessa_id: isLavoro ? commessaId || null : null,
        fase_id: isLavoro ? faseId || null : null,
        cliente_id: isVisita ? clienteId || null : null,
        risorse: risorsePayload,
        ore,
        note: note || null,
      })
      onCreated(resp.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nella creazione')
    } finally {
      setSaving(false)
    }
  }

  const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[2000] pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Nuovo Evento</h2>
            <p className="text-sm text-gray-500 capitalize">{formattedDate}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Tipo evento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Evento</label>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(TIPO_EVENTO_CONFIG) as [TipoEvento, typeof TIPO_EVENTO_CONFIG[TipoEvento]][]).map(([tipo, cfg]) => (
                <button
                  key={tipo}
                  onClick={() => setTipoEvento(tipo)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                    tipoEvento === tipo
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={tipoEvento === tipo ? { backgroundColor: cfg.color } : undefined}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cliente selector (solo per visita commerciale) */}
          {isVisita && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca cliente..."
                  value={clienteSearch}
                  onChange={e => setClienteSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredClienti.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">Nessun cliente trovato</p>
                )}
                {filteredClienti.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setClienteId(c.id)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      clienteId === c.id
                        ? 'bg-violet-50 text-violet-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{c.ragione_sociale}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Commessa selector (solo per lavoro) */}
          {isLavoro && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commessa</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca commessa..."
                  value={commessaSearch}
                  onChange={e => setCommessaSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredCommesse.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">Nessuna commessa attiva</p>
                )}
                {filteredCommesse.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setCommessaId(c.id)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      commessaId === c.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{c.numero}</span>
                    <span className="text-gray-500"> — {c.titolo}</span>
                    <span className="text-gray-400 text-xs ml-2">({c.cliente})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fase selector (solo per lavoro con commessa) */}
          {isLavoro && commessaId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fase / Task</label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {fasi.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">Nessuna fase disponibile</p>
                )}
                {fasi.map(f => {
                  const overDays = f.giorni_calendario - f.durata_prevista
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFaseId(f.id)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        faseId === f.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{f.titolo}</span>
                        <span className="text-xs text-gray-500">
                          {f.giorni_calendario}/{f.durata_prevista} gg
                        </span>
                      </div>
                      {f.completata && (
                        <span className="text-xs text-green-600">Completata</span>
                      )}
                      {overDays > 0 && !f.completata && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-amber-600">+{overDays} gg oltre il previsto</span>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Risorse selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risorse ({selectedRisorse.size} selezionate)
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca risorsa..."
                value={risorseSearch}
                onChange={e => setRisorseSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg">
              {Object.entries(risorseByGruppo).map(([gruppo, items]) => (
                <div key={gruppo}>
                  <div className="px-4 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                    {gruppo}
                  </div>
                  {items.map(r => (
                    <button
                      key={r.id}
                      onClick={() => toggleRisorsa(r.id)}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
                        selectedRisorse.has(r.id)
                          ? 'bg-primary-50 text-primary-700'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        selectedRisorse.has(r.id)
                          ? 'bg-primary-600 border-primary-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedRisorse.has(r.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span>{r.tipo === 'personale' ? '\u{1F464}' : '\u{1F527}'} {r.label}</span>
                      {r.ruolo && <span className="text-xs text-gray-400 ml-auto">{r.ruolo}</span>}
                      {r.tipologia && <span className="text-xs text-gray-400 ml-auto">{r.tipologia}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Ore + Note */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ore</label>
              <input
                type="number"
                min={0.5}
                max={24}
                step={0.5}
                value={ore}
                onChange={e => setOre(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Opzionale..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Warning fase */}
          {selectedFase && selectedFase.giorni_calendario >= selectedFase.durata_prevista && (
            <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-700">
                <p className="font-medium">Attenzione: estensione task</p>
                <p>Questo sara' il giorno {selectedFase.giorni_calendario + 1} su {selectedFase.durata_prevista} previsti. Il task verra' esteso.</p>
              </div>
            </div>
          )}

          {/* Info visita */}
          {isVisita && clienteId && (
            <div className="px-4 py-3 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-700">
              La visita verra' automaticamente registrata nel fascicolo del cliente.
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Creazione...' : 'Pianifica'}
          </button>
        </div>
      </div>
    </div>
  )
}
